import { useMemo, useState } from 'react';
import PostCard from '@/components/feature/PostCard';
import { categories } from '@/mocks/categories';
import { posts } from '@/mocks/posts';

const PAGE_SIZE = 6;

export default function LatestSection() {
  const [keyword, setKeyword] = useState('');
  const [active, setActive] = useState<string>('all');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    return posts.filter((post) => {
      const matchCategory = active === 'all' || post.categorySlug === active;
      const matchKeyword =
        !key ||
        [post.title, post.excerpt, post.tags.join(' ')].join(' ').toLowerCase().includes(key);
      return matchCategory && matchKeyword;
    });
  }, [keyword, active]);

  const shown = filtered.slice(0, visible);

  const handleCategory = (slug: string) => {
    setActive(slug);
    setVisible(PAGE_SIZE);
  };

  return (
    <section id="latest" className="w-full bg-background-50 scroll-mt-20">
      <div className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div>
            <span className="text-xs font-medium text-primary-600 tracking-widest">
              LATEST / 最新整理
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              最新更新的分类合集
            </h2>
          </div>

          <div className="relative w-full lg:w-80">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setVisible(PAGE_SIZE);
              }}
              placeholder="搜索话题，如 直播 / 短剧 / 音乐节"
              aria-label="搜索话题"
              className="w-full h-11 pl-10 pr-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
            />
          </div>
        </div>

        <div className="inline-flex flex-wrap gap-1 p-1 rounded-full border border-background-200 bg-background-100 mb-8">
          <button
            type="button"
            onClick={() => handleCategory('all')}
            className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
              active === 'all'
                ? 'bg-primary-500 text-background-50 font-medium'
                : 'text-foreground-700 hover:text-primary-700'
            }`}
          >
            全部分类
          </button>
          {categories.map((category) => (
            <button
              key={category.slug}
              type="button"
              onClick={() => handleCategory(category.slug)}
              className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                active === category.slug
                  ? 'bg-primary-500 text-background-50 font-medium'
                  : 'text-foreground-700 hover:text-primary-700'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {shown.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {shown.map((post) => (
              <PostCard key={post.slug} post={post} variant="default" />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
            <p className="mt-3 text-sm text-foreground-600">
              没有找到匹配的话题，换个关键词试试
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setActive('all');
              }}
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        )}

        {visible < filtered.length && (
          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-foreground-300 text-foreground-900 text-sm font-medium hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-down-line"></i>
              加载更多话题（还剩 {filtered.length - visible} 篇）
            </button>
          </div>
        )}
      </div>
    </section>
  );
}